import { Check, Crown, XCircle } from 'lucide-react'
import BracketPlayerNames from './BracketPlayerNames'
import { cn } from '../../lib/utils'

/**
 * A single pool-play match: two team rows; click a row to pick that team
 * as the winner. Used inside <PoolGrid /> and <SundayMiniBracket />.
 *
 * Props:
 *  - match: { match_id, round, match_number }
 *  - teamOne, teamTwo: team objects with { id, players, pool_seed/seed }
 *  - teamSize: division team size
 *  - pick: { teamId, winner_team_id }
 *  - canEdit: whether picks can be modified
 *  - isViewingOfficial: viewing the official entry
 *  - onSelectWinner(match, teamId)
 *  - placeholder: optional text shown when teams are TBD (Sunday before standings ready)
 *  - isChampionPick: when true (used by Sunday/Finals mini-bracket), show a Crown
 *    on the user's picked winner. Each Finals match's winner is effectively a
 *    "champion" pick — mirrors the standard bracket's champion crown.
 */
export default function PoolMatchCard({
  match,
  teamOne,
  teamTwo,
  teamSize = 1,
  pick,
  canEdit,
  isViewingOfficial,
  onSelectWinner,
  placeholder,
  isChampionPick = false,
}) {
  const officialWinnerId = pick?.winner_team_id || null
  const userTeamId = pick?.teamId || null

  const renderRow = (team, isTopRow) => {
    const isTBD = !team
    const isPicked = !isTBD && userTeamId === team.id
    const isOfficial = !isTBD && officialWinnerId === team.id
    const isCorrect = !isViewingOfficial && isPicked && isOfficial
    const isIncorrect = !isViewingOfficial && isPicked && officialWinnerId && officialWinnerId !== team.id
    // DUB-169: a picked team with no official result yet only showed a faint
    // purple tint, which Amy read as a random color rather than "my pick."
    // Strengthen that pending state with a clear inset ring (+ the existing
    // crown on Finals) so the user's selection is unmistakable. Resolved picks
    // already carry the check/X, so the ring is reserved for the pending state.
    const isPendingPick = isPicked && !isCorrect && !isIncorrect
    // DUB-169 (consistency with single-elim): the official winner you did NOT
    // pick is shown bold + grey dot — exactly like the actual-winner cell in
    // standard brackets. The matchups are pre-set, so no extra name row is
    // needed (unlike single-elim's added winner cell).
    const isOfficialWinnerNotPicked = !isViewingOfficial && isOfficial && !isPicked

    const onClick = () => {
      if (!canEdit || isTBD) return
      onSelectWinner?.(match, team.id)
    }

    return (
      <button
        type="button"
        onClick={onClick}
        disabled={!canEdit || isTBD}
        className={cn(
          'flex items-center gap-2 px-2 py-2 text-left transition-colors w-full',
          isTopRow ? 'border-b' : '',
          isPicked && 'bg-primary/10 font-semibold',
          isPendingPick && 'ring-2 ring-inset ring-primary/60',
          isCorrect && 'bg-emerald-500/15',
          isIncorrect && 'bg-rose-500/10',
          // DUB-169: the official winner you DIDN'T pick used to get a faint
          // purple tint (bg-primary/5), which read as "random color" and
          // collided with the purple used for your own pick. Amy 2026-06-06:
          // leave it plain — your wrong pick's red ✗ already implies the other
          // team won. Purple now means "your pick" and nothing else.
          // Owners 2026-05-22: no hover affordance on pool/Finals rows
          // (DUB-141 reverted). Picking still bolds + colors the cell.
          (!canEdit || isTBD) && 'cursor-default',
        )}
      >
        {/* DUB-133: pool-play shows NO seed/slot numbers — pool play isn't
            seeded. Keep a small leading marker only for TBD slots so the
            empty state still reads. */}
        {isTBD && (
          <span className="w-6 shrink-0 font-mono text-xs text-muted-foreground text-center">
            —
          </span>
        )}
        <div className="flex-1 min-w-0">
          {isTBD ? (
            <span className="text-sm italic text-muted-foreground">{placeholder || 'TBD'}</span>
          ) : (
            <BracketPlayerNames
              team={team}
              teamSize={teamSize}
              className={cn(
                isIncorrect && 'line-through',
                isOfficialWinnerNotPicked && 'font-semibold',
              )}
            />
          )}
        </div>
        {isCorrect && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
        {isIncorrect && <XCircle className="h-4 w-4 text-rose-600 shrink-0" />}
        {isOfficialWinnerNotPicked && (
          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-[var(--mauve-8)]" />
        )}
        {/* Tickets f6d5885c / faf16b6a: a lost champion pick keeps its Crown,
            turned red to match the strikethrough and ✗. */}
        {isChampionPick && isPicked && (
          <Crown
            className={cn(
              'h-4 w-4 shrink-0',
              isIncorrect ? 'text-[var(--color-pick-wrong-text)]' : 'text-[var(--color-crown)]',
            )}
          />
        )}
      </button>
    )
  }

  return (
    <div
      className="rounded-md border bg-background overflow-hidden transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-lg"
      data-pool-match-id={match.match_id}
    >
      {renderRow(teamOne, true)}
      {renderRow(teamTwo, false)}
    </div>
  )
}
